import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    AccessTime as AccessTimeIcon,
    Circle as CircleIcon,
    Delete as DeleteIcon,
    DeleteSweep as DeleteSweepIcon,
    Edit as EditIcon,
    Group as GroupIcon,
    Home as HomeIcon,
    MonitorHeart as MonitorHeartIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon,
    Splitscreen as SplitscreenIcon,
    TextSnippet as TextSnippetIcon,
    VideoFile as VideoFileIcon
} from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import { format, formatDistanceToNow } from 'date-fns';
import Swal from 'sweetalert2';
import SessionManagementService from '../../services/SessionManagementService';
import { PageHeader } from '../common/PageHeader';

const ActiveSessionsPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [allUserSessions, setAllUserSessions] = useState([]);
  const [loggedInUsers, setLoggedInUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  
  useEffect(() => {
    // Check if user is admin
    const userData = JSON.parse(localStorage.getItem('authUser')) || {};
    const userType = userData.userType || '';
    setIsAdmin(userType === 'admin' || userType === 'teamlead');
    
    fetchActiveSessions();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const fetchActiveSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      // Fetch user's own sessions
      const response = await SessionManagementService.getActiveSessions(
        authUser.email,
        authToken
      );
      
      setSessions(response.data || []);
      
      // If admin, fetch all users' sessions and currently logged-in users
      const userData = JSON.parse(localStorage.getItem('authUser')) || {};
      const userType = userData.userType || '';
      if (userType === 'admin' || userType === 'teamlead') {
        const allSessionsResponse = await SessionManagementService.getAllUsersSessions(
          authUser.email,
          authToken
        );
        setAllUserSessions(allSessionsResponse.data || []);

        try {
          const loggedInResponse = await SessionManagementService.getLoggedInUsers(
            authUser.email,
            authToken
          );
          setLoggedInUsers(loggedInResponse.data || []);
        } catch (e) {
          // Non-critical – don't fail the whole page
          console.error('Could not fetch logged-in users:', e);
        }
      }
    } catch (err) {
      console.error("Error fetching active sessions:", err);
      setError("Failed to fetch active sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleEndSession = async (videoId, languageName, userEmail = null) => {
    try {
      setLoading(true);
      
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      if (userEmail && userEmail !== authUser.email) {
        // Admin ending someone else's session
        await SessionManagementService.endUserSession(
          videoId,
          languageName,
          userEmail,
          authUser.email,
          authToken
        );
        setSuccess(`Session ended successfully for ${userEmail} on ${languageName}`);
      } else {
        // User ending their own session
        await SessionManagementService.endSession(
          videoId,
          languageName,
          authUser.email, 
          authToken
        );
        setSuccess(`Session ended successfully for ${languageName}`);
      }
      
      // Refresh the sessions list
      fetchActiveSessions();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Error ending session:", error);
      setError("Failed to end session: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleEndAllSessions = () => {
    Swal.fire({
      title: 'End All Sessions?',
      text: "This will end all your active editing sessions. You'll need to reopen any files you were editing. Continue?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, end all sessions'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          
          const authUser = JSON.parse(localStorage.getItem('authUser'));
          const authToken = JSON.parse(localStorage.getItem('authToken'));
          
          await SessionManagementService.endAllSessions(
            authUser.email,
            authToken
          );
          
          setSuccess("All sessions ended successfully");
          
          // Refresh the sessions list
          fetchActiveSessions();
          
          // Clear success message after 3 seconds
          setTimeout(() => {
            setSuccess(null);
          }, 3000);
        } catch (error) {
          console.error("Error ending all sessions:", error);
          setError("Failed to end all sessions: " + (error.response?.data?.message || error.message));
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  const handleGoBack = () => {
    navigate(-1); // Go back to the previous page
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm:ss a');
    } catch (e) {
      return dateStr;
    }
  };
  
  const getTimeSince = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };
  
  // Calculate session duration
  const getSessionDuration = (startDate, lastActivity) => {
    if (!startDate) return 'Unknown';
    
    const start = new Date(startDate);
    const end = lastActivity ? new Date(lastActivity) : new Date();
    const diffMs = end - start;
    
    // Format duration as hh:mm:ss
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Render a sessions table (reused for both user's sessions and all users' sessions)
  const renderSessionsTable = (sessionsList, showUserColumn = false) => {
    if (sessionsList.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }} className="sessions-list" >
          <VideoFileIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Active Sessions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {showUserColumn 
              ? "There are no active editing sessions across all users."
              : "You don't have any active editing sessions at the moment."}
          </Typography>
          
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {showUserColumn && <TableCell>User</TableCell>}
              <TableCell>File Name</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Editor</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessionsList.map((session) => (
              <TableRow 
                key={`${session.fileId || session.videoId}-${session.languageName}${session.sectionId ? '-' + session.sectionId : ''}${showUserColumn ? '-' + session.email : ''}`}
                sx={{ 
                  backgroundColor: session.sectionId ? 'rgba(33, 150, 243, 0.04)' : 'inherit'
                }}
              >
                {showUserColumn && (
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">{session.email}</Typography>
                    </Box>
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {session.videoName || 'Unknown Video'}
                  </Typography>
                  {session.folderPath && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {session.folderPath}
                    </Typography>
                  )}
                  {session.sectionName && (
                    <Typography 
                      variant="caption" 
                      color="primary" 
                      display="block" 
                      sx={{ mt: 0.5, fontWeight: 'medium' }}
                    >
                      Section: {session.sectionName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={session.languageName} 
                    size="small" 
                    sx={{
                        color: 'secondary.main'
                    }}
                    variant="outlined" 
                  />
                </TableCell>
                <TableCell>
                  {session.sectionId ? (
                    <Tooltip title="Section Editing">
                      <Chip
                        icon={<SplitscreenIcon fontSize="small" />}
                        label="Section"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : session.fileType === 'textfile' ? (
                    <Tooltip title="Text File Editing">
                      <Chip
                        icon={<TextSnippetIcon fontSize="small" />}
                        label="Text File"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Full Subtitle Editing">
                      <Chip
                        icon={<VideoFileIcon fontSize="small" />}
                        label="Full"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={session.editorVersion || 'V1'}
                    size="small"
                    color={session.editorVersion === 'V2' ? 'success' : 'default'}
                    variant={session.editorVersion === 'V2' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={formatDateTime(session.startedAt)}>
                    <Box>
                      <Typography variant="body2">
                        {getTimeSince(session.startedAt)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={session.lastActivity ? formatDateTime(session.lastActivity) : 'No activity recorded'}>
                    <Box>
                      <Typography variant="body2">
                        {session.lastActivity ? getTimeSince(session.lastActivity) : 'N/A'}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Session duration">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {getSessionDuration(session.startedAt, session.lastActivity)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="End Session">
                    <IconButton 
                      color="error"
                      onClick={() => {
                        if (session.sectionId) {
                          handleEndSectionSession(
                            session.videoId, 
                            session.languageName,
                            session.sectionId,
                            showUserColumn ? session.email : null
                          );
                        } else if (session.fileType === 'textfile') {
                          handleEndTextFileSession(
                            session.fileId || session.videoId,
                            session.languageName,
                            showUserColumn ? session.email : null
                          );
                        } else {
                          handleEndSession(
                            session.videoId, 
                            session.languageName, 
                            showUserColumn ? session.email : null
                          );
                        }
                      }}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Add a new function to handle ending section editing sessions
  const handleEndSectionSession = async (videoId, languageName, sectionId, userEmail = null) => {
    try {
      setLoading(true);
      
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      if (userEmail && userEmail !== authUser.email) {
        // Admin ending someone else's section session
        await SessionManagementService.endUserSectionSession(
          videoId,
          languageName,
          sectionId,
          userEmail,
          authUser.email,
          authToken
        );
        setSuccess(`Section session ended successfully for ${userEmail} on ${languageName}`);
      } else {
        // User ending their own section session
        await SessionManagementService.endSectionSession(
          videoId,
          languageName,
          sectionId,
          authUser.email, 
          authToken
        );
        setSuccess(`Section session ended successfully for ${languageName}`);
      }
      
      // Refresh the sessions list
      fetchActiveSessions();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Error ending section session:", error);
      setError("Failed to end section session: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEndTextFileSession = async (fileId, languageName, userEmail = null) => {
    try {
      setLoading(true);

      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));

      if (userEmail && userEmail !== authUser.email) {
        await SessionManagementService.endUserTextFileSession(
          fileId, languageName, userEmail, authUser.email, authToken
        );
        setSuccess(`Text file session ended for ${userEmail} on ${languageName}`);
      } else {
        await SessionManagementService.endTextFileSession(
          fileId, languageName, authUser.email, authToken
        );
        setSuccess(`Text file session ended for ${languageName}`);
      }

      fetchActiveSessions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error ending text file session:', error);
      setError('Failed to end text file session: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}>
      <Paper sx={{
          borderRadius: 0,
          padding: 0
        }}>
      <PageHeader 
        darkMode={darkMode} 
        toggleDarkMode={toggleDarkMode}
        themeVariant={themeVariant} 
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, padding: 1}}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<HomeIcon />}
          onClick={() => navigate('/')}
          sx={{ mr: 2 }}
        >
          Home
        </Button>
        {/* Add arrow icon between buttons */}
        <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
        <Button
          variant="text"
          color="secondary"
          startIcon={<MonitorHeartIcon />}
          sx={{ mr: 2 }}
        >
          System
        </Button>
        <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
        <Button
          variant="text"
          color="secondary"
          startIcon={<EditIcon />}
          sx={{ mr: 2 }}
        >
          Active Editing Sessions
        </Button>
      </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          
          <Button 
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchActiveSessions}
            sx={{ ml: 'auto' }}
            className="refresh-button"
          >
            Refresh
          </Button>
        </Box>
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {isAdmin && (
          <Box sx={{ mb: 2 }}>
            <Tabs 
              value={activeTab}
              onChange={handleTabChange}
              aria-label="session tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              className="session-tabs"
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    My Sessions
                    <Badge 
                      badgeContent={sessions.length} 
                      color="primary" 
                      sx={{ ml: 1, padding: '4px 4px' }}
                    />
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon sx={{ mr: 1 }} />
                    All Users' Sessions
                    <Badge 
                      badgeContent={allUserSessions.length} 
                      color="secondary" 
                      sx={{ ml: 1 , padding: '4px 4px'}}
                    />
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircleIcon sx={{ mr: 1, fontSize: 12, color: 'success.main' }} />
                    Currently Online
                    <Badge 
                      badgeContent={loggedInUsers.length} 
                      color="success" 
                      sx={{ ml: 1, padding: '4px 4px' }}
                    />
                  </Box>
                }
              />
            </Tabs>
          </Box>
        )}
        
        {sessions.length > 0 && activeTab === 0 && (
          <Box 
               sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              You have {sessions.length} active editing {sessions.length === 1 ? 'session' : 'sessions'}
            </Typography>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleEndAllSessions}
            >
              End All Sessions
            </Button>
          </Box>
        )}
        
        {isAdmin && activeTab === 1 && allUserSessions.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              There {allUserSessions.length === 1 ? 'is' : 'are'} {allUserSessions.length} active editing {allUserSessions.length === 1 ? 'session' : 'sessions'} across all users
            </Typography>
          </Box>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!isAdmin && renderSessionsTable(sessions)}
            
            {isAdmin && activeTab === 0 && renderSessionsTable(sessions)}
            
            {isAdmin && activeTab === 1 && renderSessionsTable(allUserSessions, true)}

            {isAdmin && activeTab === 2 && (
              loggedInUsers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <GroupIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>No Users Currently Online</Typography>
                  <Typography variant="body1" color="text.secondary">
                    No users have been active in the last 30 minutes.
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Last Seen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loggedInUsers.map((user) => (
                        <TableRow key={user.email}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'success.main' }}>
                                {(user.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
                              </Avatar>
                              <Typography variant="body2">{user.email}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.firstName || user.lastName
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.userType || 'user'}
                              size="small"
                              color={
                                user.userType === 'admin' || user.userType === 'super_admin'
                                  ? 'primary'
                                  : user.userType === 'dubber'
                                  ? 'secondary'
                                  : 'default'
                              }
                              variant="filled"
                              sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : ''}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircleIcon sx={{ fontSize: 10, color: 'success.main' }} />
                                <Typography variant="body2">
                                  {user.lastLoginAt ? getTimeSince(user.lastLoginAt) : 'N/A'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </>
        )}
        
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="subtitle2">About Active Sessions</Typography>
            <Typography variant="body2">
              Active sessions are created when you edit subtitle files. Closing your browser normally ends these sessions automatically, 
              but sometimes they may remain active if the browser closes unexpectedly. Sessions automatically expire after 15 minutes of inactivity.
            </Typography>
          </Alert>
        </Box>
        </Paper>
      </Box>
  );
};

export default ActiveSessionsPage;